import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    CardText,
    Button,
    Badge,
    Progress,
    Spinner,
    Alert,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import { supabase } from '../../lib/supabase';
import {
    FiTrendingUp,
    FiTrendingDown,
    FiEye,
    FiEdit,
    FiMoreVertical,
    FiDownload,
    FiFilter,
    FiRefreshCw,
    FiAlertTriangle,
    FiWifiOff,
    FiServer,
    FiCloudOff,
    FiDollarSign,
    FiTarget,
    FiBarChart2,
    FiBell,
    FiUsers,
    FiClock
} from 'react-icons/fi';

const DashboardPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [alert, setAlert] = useState({ visible: false, message: '', type: '' });
    const [error, setError] = useState(null);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [timeRange, setTimeRange] = useState('7d');
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [connectionError, setConnectionError] = useState(false);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    useEffect(() => {
        loadDashboardData();
        const interval = setInterval(() => {
            if (isOnline && !connectionError) {
                refreshDashboardData(true);
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [isOnline]);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError(null);
            setConnectionError(false);

            if (!isOnline) {
                throw new Error('No internet connection. Please check your network and try again.');
            }

            const cachedData = localStorage.getItem('advert_dashboard_data');
            const lastFetch = localStorage.getItem('last_dashboard_fetch');

            if (cachedData && lastFetch) {
                const cacheTime = new Date(lastFetch).getTime();
                const currentTime = new Date().getTime();

                if (currentTime - cacheTime < 5 * 60 * 1000) {
                    const parsedData = JSON.parse(cachedData);
                    setDashboardData(parsedData);
                    setLoading(false);
                    setLastRefresh(new Date(lastFetch));
                    if (isOnline) {
                        setTimeout(() => refreshDashboardData(true), 1000);
                    }
                    return;
                }
            }

            await refreshDashboardData();

        } catch (err) {
            console.error('Error loading dashboard:', err);

            if (err.message.includes('internet') || err.message.includes('network') || !isOnline) {
                setConnectionError(true);
                setError('Network Error: Please check your internet connection.');
            } else if (err.message.includes('auth') || err.message.includes('session')) {
                setError('Authentication Error: Please login again.');
            } else {
                setError(`Error: ${err.message}`);
            }

            setLoading(false);

            const cachedData = localStorage.getItem('advert_dashboard_data');
            if (cachedData) {
                try {
                    const parsedData = JSON.parse(cachedData);
                    setDashboardData(parsedData);
                    showAlert('Using cached data. Some information may be outdated.', 'warning');
                } catch (parseError) {
                    console.error('Error parsing cached data:', parseError);
                }
            }
        }
    };

    const refreshDashboardData = async (isBackgroundRefresh = false) => {
        try {
            if (!isBackgroundRefresh) {
                setLoading(true);
            }

            setError(null);
            setConnectionError(false);

            if (!isOnline) {
                throw new Error('No internet connection');
            }

            const { data: { user }, error: userError } = await supabase.auth.getUser();

            if (userError || !user) {
                throw new Error('Authentication required. Please login again.');
            }

            // Simulate API call - Replace with actual API
            await new Promise(resolve => setTimeout(resolve, 500));

            const mockData = {
                user: {
                    id: user.id,
                    profile: { full_name: user.email?.split('@')[0] || 'User' },
                    email: user.email,
                    last_login: new Date().toISOString()
                },
                financial: {
                    balance: 12500.75,
                    currency: 'USD',
                    total_spend: 2450.50,
                    total_budget: 10000,
                    available_balance: 12500.75,
                    daily_change: 2.5,
                    weekly_change: 8.3
                },
                campaigns: {
                    list: [
                        { id: 1, name: 'Summer Sale Campaign', status: 'active', type: 'Display', budget: 5000, spent: 2450, ctr: 3.2 },
                        { id: 2, name: 'Product Launch', status: 'paused', type: 'Social', budget: 3000, spent: 1200, ctr: 2.1 },
                        { id: 3, name: 'Brand Awareness', status: 'draft', type: 'Video', budget: 10000, spent: 0, ctr: 0 },
                        { id: 4, name: 'Retargeting Campaign', status: 'completed', type: 'Search', budget: 8000, spent: 8000, ctr: 4.5 }
                    ],
                    summary: {
                        total: 4,
                        active: 1,
                        paused: 1,
                        draft: 1,
                        completed: 1
                    },
                    performance: {
                        total_impressions: 124500,
                        total_clicks: 3870,
                        overall_ctr: 3.1,
                        total_conversions: 245,
                        avg_cpc: 2.45
                    }
                },
                notifications: {
                    messages: [
                        { id: 1, title: 'Campaign Live', message: 'Your campaign is now active', time: '2 hours ago', read: false },
                        { id: 2, title: 'Budget Alert', message: 'Campaign budget at 80%', time: '5 hours ago', read: false },
                        { id: 3, title: 'Performance Update', message: 'CTR improved by 15%', time: '1 day ago', read: true }
                    ],
                    unread_count: 2,
                    has_unread: true
                },
                analytics: {
                    daily_spend: [
                        { day: 'Mon', amount: 450 },
                        { day: 'Tue', amount: 620 },
                        { day: 'Wed', amount: 380 },
                        { day: 'Thu', amount: 510 },
                        { day: 'Fri', amount: 490 },
                        { day: 'Sat', amount: 320 },
                        { day: 'Sun', amount: 280 }
                    ],
                    top_performing: [
                        { name: 'Display Ads', conversions: 1245, change: 5.2 },
                        { name: 'Social Media', conversions: 845, change: 3.8 },
                        { name: 'Search Ads', conversions: 620, change: 2.1 },
                        { name: 'Video Ads', conversions: 420, change: -1.5 }
                    ],
                    recent_activity: [
                        { type: 'campaign', action: 'Created', name: 'Summer Sale', time: '10:30 AM' },
                        { type: 'ad', action: 'Updated', name: 'Product Banner', time: 'Yesterday' },
                        { type: 'budget', action: 'Increased', name: 'Brand Awareness', time: '2 days ago' }
                    ]
                }
            };

            setDashboardData(mockData);
            localStorage.setItem('advert_dashboard_data', JSON.stringify(mockData));
            localStorage.setItem('last_dashboard_fetch', new Date().toISOString());
            setLastRefresh(new Date());

            if (!isBackgroundRefresh) {
                showAlert('Dashboard data refreshed successfully', 'success');
            }

        } catch (err) {
            console.error('Refresh error:', err);

            let errorMessage = err.message || 'Failed to refresh dashboard data';
            let errorType = 'error';

            if (err.message.includes('timeout')) {
                errorMessage = 'Server timeout. Please try again later.';
                errorType = 'timeout';
            } else if (err.message.includes('internet') || err.message.includes('network')) {
                errorMessage = 'Network error. Please check your connection.';
                errorType = 'network';
                setConnectionError(true);
            } else if (err.message.includes('auth') || err.message.includes('login')) {
                errorMessage = 'Session expired. Please login again.';
                errorType = 'auth';
            }

            if (!isBackgroundRefresh) {
                setError(errorMessage);
                showAlert(`Could not refresh data. ${errorMessage}`, 'warning');
            }

            if (!dashboardData) {
                const cachedData = localStorage.getItem('advert_dashboard_data');
                if (cachedData) {
                    try {
                        const parsedData = JSON.parse(cachedData);
                        setDashboardData(parsedData);
                    } catch (parseError) {
                        console.error('Error parsing cached data:', parseError);
                    }
                }
            }
        } finally {
            if (!isBackgroundRefresh) {
                setLoading(false);
            }
        }
    };

    const getStatusColor = (status) => {
        if (!status) return 'secondary';
        switch (status.toLowerCase()) {
            case 'active': return 'success';
            case 'paused': return 'warning';
            case 'draft': return 'secondary';
            case 'completed': return 'info';
            case 'pending': return 'primary';
            default: return 'secondary';
        }
    };

    const showAlert = (message, type = 'info') => {
        setAlert({ visible: true, message, type });
        setTimeout(() => setAlert({ visible: false, message: '', type: '' }), 5000);
    };

    const formatCurrency = (amount, currency = 'USD') => {
        if (amount === null || amount === undefined) return '$0.00';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderErrorState = () => {
        return (
            <div className="error-state">
                <div className="error-icon mb-4">
                    {connectionError ? <FiWifiOff size={48} /> : <FiAlertTriangle size={48} />}
                </div>
                <h4 className="mb-2 fs-6 fw-semibold">
                    {connectionError ? 'Network Connection Lost' : 'Unable to Load Dashboard'}
                </h4>
                <p className="text-muted fs-7 mb-4">
                    {connectionError
                        ? 'Please check your internet connection and try again.'
                        : 'There was an error loading your dashboard data.'}
                </p>
                <div className="error-actions d-flex gap-2">
                    <Button
                        color="primary"
                        size="sm"
                        onClick={() => refreshDashboardData()}
                        disabled={loading || !isOnline}
                    >
                        {loading ? (
                            <Spinner size="sm" className="me-2" />
                        ) : (
                            <FiRefreshCw className="me-2" />
                        )}
                        Retry
                    </Button>
                </div>
            </div>
        );
    };

    const renderPerformanceChart = () => {
        const data = dashboardData?.analytics?.daily_spend || [];
        const maxAmount = Math.max(...data.map(d => d.amount), 1);

        return (
            <div className="performance-chart">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h6 className="mb-1 fw-medium">Daily Spend</h6>
                        <small className="text-muted fs-7">Last 7 days</small>
                    </div>
                    <Dropdown>
                        <DropdownToggle caret size="sm" outline className="fs-7">
                            {timeRange === '7d' ? '7 Days' : timeRange === '30d' ? '30 Days' : 'Custom'}
                        </DropdownToggle>
                        <DropdownMenu className="fs-7">
                            <DropdownItem onClick={() => setTimeRange('7d')}>Last 7 Days</DropdownItem>
                            <DropdownItem onClick={() => setTimeRange('30d')}>Last 30 Days</DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
                <div className="chart-bars d-flex align-items-end" style={{ height: '120px' }}>
                    {data.map((day, index) => (
                        <div key={index} className="chart-bar flex-fill mx-1">
                            <div
                                className="bar bg-primary rounded-top"
                                style={{
                                    height: `${(day.amount / maxAmount) * 100}%`,
                                    minHeight: '2px'
                                }}
                                title={`${day.day}: ${formatCurrency(day.amount)}`}
                            />
                            <div className="bar-label text-center small mt-2 fs-7">{day.day}</div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (loading && !dashboardData) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="text-center">
                    <Spinner color="primary" size="lg" />
                    <p className="mt-3 fs-7">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    const hasError = error || connectionError;
    const userEmail = dashboardData?.user?.email || '';
    const userName = dashboardData?.user?.profile?.full_name || userEmail?.split('@')[0] || 'User';

    return (
        <div className="dashboard-page">
            {alert.visible && (
                <Alert color={alert.type} className="fixed-top m-3 fs-7" style={{ zIndex: 1050 }}>
                    <div className="d-flex justify-content-between align-items-center">
                        <span>{alert.message}</span>
                        <Button
                            color="link"
                            size="sm"
                            className="p-0 text-white fs-7"
                            onClick={() => setAlert({ visible: false, message: '', type: '' })}
                        >
                            &times;
                        </Button>
                    </div>
                </Alert>
            )}

            {!isOnline && (
                <Alert color="warning" className="mb-3 fs-7">
                    <div className="d-flex align-items-center">
                        <FiWifiOff className="me-2" />
                        <div>
                            <strong>You are offline</strong>
                            <div className="fs-7">Some features may be unavailable</div>
                        </div>
                    </div>
                </Alert>
            )}

            {/* Header */}
            <div className="dashboard-header mb-4">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h1 className="h4 fw-semibold mb-1">Welcome back, {userName}!</h1>
                        <p className="text-muted mb-0 fs-7">
                            {lastRefresh ? `Last updated: ${formatTime(lastRefresh)}` : "Here's your advertising overview"}
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => navigate('/panel/campaigns/new')}
                            className="d-flex align-items-center fs-7"
                        >
                            <FiTarget className="me-1" size={14} />
                            New Campaign
                        </Button>
                        <Button
                            outline
                            color="secondary"
                            size="sm"
                            onClick={() => refreshDashboardData()}
                            disabled={loading || !isOnline}
                            className="fs-7"
                        >
                            {loading ? (
                                <Spinner size="sm" className="me-2" />
                            ) : (
                                <FiRefreshCw className="me-2" />
                            )}
                            Refresh
                        </Button>
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {hasError && (
                <Card className="mb-4 border-0 shadow-sm">
                    <CardBody>
                        {renderErrorState()}
                    </CardBody>
                </Card>
            )}

            {/* Stats Cards with custom border radius */}
            {dashboardData && !hasError && (
                <>
                    <Row className="mb-4 stats-row">
                        <Col xl={3} md={6} className="mb-3">
                            <Card className="h-100 border-0 shadow-sm stats-card first-card">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <CardText className="text-muted fs-7 mb-2">Account Balance</CardText>
                                            <CardTitle tag="h5" className="fw-semibold mb-2">
                                                {formatCurrency(dashboardData?.financial?.balance)}
                                            </CardTitle>
                                            <div className="d-flex align-items-center">
                                                {dashboardData?.financial?.daily_change > 0 ? (
                                                    <FiTrendingUp className="text-success me-1" size={14} />
                                                ) : (
                                                    <FiTrendingDown className="text-danger me-1" size={14} />
                                                )}
                                                <span className={`fs-7 ${dashboardData?.financial?.daily_change > 0 ? 'text-success' : 'text-danger'}`}>
                                                    {dashboardData?.financial?.daily_change > 0 ? '+' : ''}
                                                    {dashboardData?.financial?.daily_change?.toFixed(1)}% today
                                                </span>
                                            </div>
                                        </div>
                                        <div className="stat-icon bg-primary-light p-2 rounded">
                                            <FiDollarSign className="text-primary" size={20} />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={3} md={6} className="mb-3">
                            <Card className="h-100 border-0 shadow-sm stats-card middle-card">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <CardText className="text-muted fs-7 mb-2">Active Campaigns</CardText>
                                            <CardTitle tag="h5" className="fw-semibold mb-2">
                                                {dashboardData?.campaigns?.summary?.active || 0}
                                                <span className="text-muted fs-7 ms-2">
                                                    / {dashboardData?.campaigns?.summary?.total || 0}
                                                </span>
                                            </CardTitle>
                                            <div className="fs-7 text-muted">
                                                {dashboardData?.campaigns?.performance?.overall_ctr?.toFixed(1)}% CTR
                                            </div>
                                        </div>
                                        <div className="stat-icon bg-success-light p-2 rounded">
                                            <FiTarget className="text-success" size={20} />
                                        </div>
                                    </div>
                                    <Progress
                                        value={dashboardData?.campaigns?.summary?.active || 0}
                                        max={Math.max(dashboardData?.campaigns?.summary?.total || 1, 1)}
                                        className="mt-3"
                                        style={{ height: '4px' }}
                                    />
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={3} md={6} className="mb-3">
                            <Card className="h-100 border-0 shadow-sm stats-card middle-card">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <CardText className="text-muted fs-7 mb-2">Total Spend</CardText>
                                            <CardTitle tag="h5" className="fw-semibold mb-2">
                                                {formatCurrency(dashboardData?.financial?.total_spend)}
                                            </CardTitle>
                                            <div className="fs-7 text-muted">
                                                Avg CPC: {formatCurrency(dashboardData?.campaigns?.performance?.avg_cpc || 0)}
                                            </div>
                                        </div>
                                        <div className="stat-icon bg-info-light p-2 rounded">
                                            <FiBarChart2 className="text-info" size={20} />
                                        </div>
                                    </div>
                                    <div className="mt-3 fs-7 text-success">
                                        <FiTrendingUp className="me-1" size={12} />
                                        {dashboardData?.financial?.weekly_change?.toFixed(1)}% this week
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>

                        <Col xl={3} md={6} className="mb-3">
                            <Card className="h-100 border-0 shadow-sm stats-card last-card">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <CardText className="text-muted fs-7 mb-2">Notifications</CardText>
                                            <CardTitle tag="h5" className="fw-semibold mb-2">
                                                {dashboardData?.notifications?.unread_count || 0}
                                                {dashboardData?.notifications?.has_unread && (
                                                    <Badge color="danger" pill className="ms-2 fs-7">New</Badge>
                                                )}
                                            </CardTitle>
                                            <div className="fs-7 text-muted">
                                                {dashboardData?.notifications?.messages?.length || 0} total
                                            </div>
                                        </div>
                                        <div className="stat-icon bg-warning-light p-2 rounded">
                                            <FiBell className="text-warning" size={20} />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Main Content */}
                    <Row>
                        <Col lg={8} className="mb-4">
                            <Card className="border-0 shadow-sm h-100">
                                <CardBody className="p-3">
                                    {renderPerformanceChart()}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={4} className="mb-4">
                            <Card className="border-0 shadow-sm h-100">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0 fw-medium">Recent Activity</h6>
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="p-0 fs-7"
                                            onClick={() => navigate('/panel/history')}
                                        >
                                            View All
                                        </Button>
                                    </div>

                                    {dashboardData?.analytics?.recent_activity?.length > 0 ? (
                                        <div className="activity-list">
                                            {dashboardData.analytics.recent_activity.map((activity, index) => (
                                                <div key={index} className="activity-item d-flex align-items-center py-3 border-bottom">
                                                    <div className="activity-icon me-3">
                                                        <div className="bg-light rounded-circle p-2">
                                                            <FiClock size={14} className="text-muted" />
                                                        </div>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-medium fs-7">{activity.name}</div>
                                                        <small className="text-muted fs-7">{activity.action}</small>
                                                    </div>
                                                    <div className="text-end">
                                                        <small className="text-muted fs-7">{activity.time}</small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <FiClock size={24} className="mb-2 text-muted" />
                                            <p className="text-muted fs-7 mb-0">No recent activity</p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Campaigns & Performance */}
                    <Row>
                        <Col lg={6} className="mb-4">
                            <Card className="border-0 shadow-sm">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0 fw-medium">Recent Campaigns</h6>
                                        <Button
                                            color="link"
                                            size="sm"
                                            className="p-0 fs-7"
                                            onClick={() => navigate('/panel/campaigns')}
                                        >
                                            View All
                                        </Button>
                                    </div>

                                    {dashboardData?.campaigns?.list?.length > 0 ? (
                                        <div className="campaigns-list">
                                            {dashboardData.campaigns.list.slice(0, 4).map((campaign, index) => (
                                                <div key={index} className="campaign-item mb-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <div>
                                                            <strong className="fs-7">{campaign.name}</strong>
                                                            <div className="text-muted fs-7">
                                                                {campaign.type} • {formatCurrency(campaign.spent)} spent
                                                            </div>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Badge color={getStatusColor(campaign.status)} pill className="fs-7">
                                                                {campaign.status}
                                                            </Badge>
                                                            <Dropdown>
                                                                <DropdownToggle color="link" size="sm" className="p-0">
                                                                    <FiMoreVertical size={16} />
                                                                </DropdownToggle>
                                                                <DropdownMenu end className="fs-7">
                                                                    <DropdownItem onClick={() => navigate(`/panel/campaigns/${campaign.id}`)}>
                                                                        <FiEye className="me-2" size={14} /> View
                                                                    </DropdownItem>
                                                                    <DropdownItem onClick={() => navigate(`/panel/campaigns/${campaign.id}/edit`)}>
                                                                        <FiEdit className="me-2" size={14} /> Edit
                                                                    </DropdownItem>
                                                                </DropdownMenu>
                                                            </Dropdown>
                                                        </div>
                                                    </div>
                                                    <Progress
                                                        value={((campaign.spent || 0) / (campaign.budget || 1)) * 100}
                                                        className="mb-2"
                                                        style={{ height: '3px' }}
                                                    />
                                                    <div className="d-flex justify-content-between fs-7 text-muted">
                                                        <span>Spent: {formatCurrency(campaign.spent)}</span>
                                                        <span>Budget: {formatCurrency(campaign.budget)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <FiTarget size={24} className="mb-2 text-muted" />
                                            <p className="text-muted fs-7 mb-3">No campaigns yet</p>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                onClick={() => navigate('/panel/campaigns/new')}
                                                className="fs-7"
                                            >
                                                Create Campaign
                                            </Button>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        <Col lg={6} className="mb-4">
                            <Card className="border-0 shadow-sm h-100">
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0 fw-medium">Top Performing Channels</h6>
                                        <Dropdown>
                                            <DropdownToggle caret size="sm" outline className="fs-7">
                                                <FiFilter size={12} className="me-1" />
                                                Filter
                                            </DropdownToggle>
                                            <DropdownMenu className="fs-7">
                                                <DropdownItem>All Channels</DropdownItem>
                                                <DropdownItem>Last 7 Days</DropdownItem>
                                                <DropdownItem>Last 30 Days</DropdownItem>
                                            </DropdownMenu>
                                        </Dropdown>
                                    </div>

                                    {dashboardData?.analytics?.top_performing?.length > 0 ? (
                                        <div className="channels-list">
                                            {dashboardData.analytics.top_performing.map((channel, index) => (
                                                <div key={index} className="channel-item d-flex align-items-center py-3 border-bottom">
                                                    <div className="channel-icon me-3">
                                                        <div className="bg-light rounded p-2">
                                                            <FiUsers size={16} className="text-muted" />
                                                        </div>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <div className="fw-medium fs-7">{channel.name}</div>
                                                        <small className="text-muted fs-7">{channel.conversions} conversions</small>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fw-medium fs-7">{formatCurrency(channel.conversions)}</div>
                                                        <small className={channel.change > 0 ? 'text-success' : 'text-danger'}>
                                                            {channel.change > 0 ? '+' : ''}{channel.change}%
                                                        </small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <FiBarChart2 size={24} className="mb-2 text-muted" />
                                            <p className="text-muted fs-7 mb-0">No performance data available</p>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </>
            )}

            <style jsx>{`
                .dashboard-page {
                    padding: 1.5rem;
                }

                .dashboard-header {
                    margin-bottom: 2rem;
                }

                /* Stats Cards with custom border radius for xl screens */
                .stats-row {
                    position: relative;
                }

                .stats-card {
                    transition: all 0.2s ease;
                    border: 1px solid #f0f0f0 !important;
                }

                .stats-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
                }

                /* Extra large screens (>= 1200px) */
                @media (min-width: 1200px) {
                    .stats-card.first-card {
                        border-radius: 20px 0 0 20px !important;
                        border-right: 1px solid #f0f0f0 !important;
                    }

                    .stats-card.middle-card {
                        border-radius: 0 !important;
                        border-left: none !important;
                        border-right: 1px solid #f0f0f0 !important;
                    }

                    .stats-card.middle-card + .middle-card {
                        border-left: 1px solid #f0f0f0 !important;
                        border-right: 1px solid #f0f0f0 !important;
                    }

                    .stats-card.last-card {
                        border-radius: 0 20px 20px 0 !important;
                        border-left: 1px solid #f0f0f0 !important;
                    }

                    /* Remove shadow between connected cards */
                    .stats-card.first-card,
                    .stats-card.middle-card,
                    .stats-card.last-card {
                        box-shadow: none !important;
                    }

                    /* Add shadow to the entire row on hover */
                    .stats-row:hover .stats-card {
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08) !important;
                    }
                }

                /* Large screens (992px - 1199px) and below */
                @media (max-width: 1199.98px) {
                    .stats-card {
                        border-radius: 12px !important;
                        margin-bottom: 1rem;
                    }
                }

                /* Medium screens (768px - 991px) */
                @media (min-width: 768px) and (max-width: 991.98px) {
                    .stats-card {
                        border-radius: 12px !important;
                        margin-bottom: 1rem;
                    }
                }

                /* Small screens (below 768px) */
                @media (max-width: 767.98px) {
                    .stats-card {
                        border-radius: 12px !important;
                        margin-bottom: 1rem;
                    }
                }

                /* Activity and Campaign Items */
                .activity-item,
                .campaign-item,
                .channel-item {
                    transition: background-color 0.15s ease;
                }

                .activity-item:hover,
                .campaign-item:hover,
                .channel-item:hover {
                    background-color: #f8f9fa;
                }

                .activity-icon .bg-light,
                .channel-icon .bg-light {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                /* Progress bars */
                .progress {
                    background-color: #f0f0f0;
                    border-radius: 2px;
                }

                .progress-bar {
                    border-radius: 2px;
                }

                /* Performance Chart */
                .performance-chart {
                    padding: 1rem 0;
                }

                .chart-bar {
                    transition: opacity 0.15s ease;
                }

                .chart-bar:hover .bar {
                    opacity: 0.8;
                }

                .bar-label {
                    font-size: 0.8125rem !important;
                    color: #6c757d;
                }

                /* Error State */
                .error-state {
                    text-align: center;
                    padding: 2rem 1rem;
                }

                .error-icon {
                    color: #6c757d;
                    margin-bottom: 1rem;
                }

                /* Badges */
                .badge {
                    font-weight: 500;
                    letter-spacing: 0.3px;
                }

                /* Icons with light backgrounds */
                .bg-primary-light {
                    background-color: rgba(13, 110, 253, 0.08);
                }

                .bg-success-light {
                    background-color: rgba(25, 135, 84, 0.08);
                }

                .bg-info-light {
                    background-color: rgba(13, 202, 240, 0.08);
                }

                .bg-warning-light {
                    background-color: rgba(255, 193, 7, 0.08);
                }

                /* Typography */
                h1, h2, h3, h4, h5, h6 {
                    color: #212529;
                }

                .text-muted {
                    color: #6c757d !important;
                }

                /* Buttons */
                .btn-sm {
                    padding: 0.25rem 0.75rem;
                    font-size: 0.8125rem;
                }

                /* Responsive adjustments */
                @media (max-width: 576px) {
                    .dashboard-page {
                        padding: 1rem;
                    }

                    .dashboard-header {
                        margin-bottom: 1.5rem;
                    }

                    .stats-card .card-body {
                        padding: 1rem !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default DashboardPage;